import { type Reference, useApolloClient } from '@apollo/client'
import { useCallback } from 'react'

import { STORY_GROUPS } from '@/constants/STORY_GROUPS.ts'
import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { STORY_TYPES } from '@/constants/STORY_TYPES.ts'
import { useUserContext } from '@/contexts/UserContext.tsx'
import { getStory } from '@/requests/apollo/cache.ts'
import { getMutation } from '@/requests/apollo/mutation.ts'
import { STORY_ORDER_COMMAND_META } from '@/requests/apollo/schema.ts'
import { getExecuteTemplate } from '@/requests/apollo/templates.ts'
import { useQueryProjectMeta } from '@/requests/queries/useQueryProject.ts'
import { getSpliceIndex } from '@/utils/array.ts'
import { getGroupNameFromKey, isGroup } from '@/utils/key.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'
import { getGroupName } from '@/utils/status.ts'
import { getStatusByGroup } from '@/utils/story.ts'
import type { ModifierDetails } from '@apollo/client/cache'

export const useMoveStory = () => {
  // biome-ignore lint/style/noNonNullAssertion: This is guaranteed to be non-null
  const user = useUserContext().user!
  const { projectMeta } = useQueryProjectMeta()
  const { cache } = useApolloClient()

  const [execute] = getMutation('Story', STORY_ORDER_COMMAND_META)

  const redefineStoryOrder = useCallback(
    (projectId: string) => {
      cache.modify({
        id: cache.identify({ __typename: 'Project', id: projectId }),
        fields: {
          // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
          stories(existingEntityRefs = [], { readField }) {
            const refs = existingEntityRefs as Reference[]

            let lastPushedGroup: StoryGroup = STORY_GROUPS.ACCEPTED

            const groupedStories = refs.reduce(
              (acc, ref) => {
                const storyType = readField('storyType', ref)
                if (storyType === STORY_TYPES.RELEASE) {
                  if (readField('subgroup', ref)) {
                    acc.unscheduled.push(ref)
                    return acc
                  }
                  acc[lastPushedGroup].push(ref)
                  return acc
                }
                const status = readField('status', ref)
                if (!status) return acc
                const { group: listName } = getGroupName(status as StoryStatus)
                acc[listName].push(ref)
                if (listName !== STORY_GROUPS.UNSCHEDULED) {
                  lastPushedGroup = listName
                }
                return acc
              },
              {
                accepted: [] as Reference[],
                current: [] as Reference[],
                scheduled: [] as Reference[],
                unscheduled: [] as Reference[],
              },
            )

            const newRefLists = [
              [...groupedStories.accepted, ...groupedStories.current, ...groupedStories.scheduled],
              groupedStories.unscheduled,
            ]

            for (const newRefList of newRefLists) {
              for (let index = 0; index < newRefList.length; index++) {
                const currentRef = newRefList[index]
                const previousRef = index > 0 ? newRefList[index - 1] : null
                const nextRef = index < newRefList.length - 1 ? newRefList[index + 1] : null

                cache.modify({
                  id: cache.identify(currentRef),
                  fields: {
                    afterId: () => (previousRef ? readField('id', previousRef) : null),
                    beforeId: () => (nextRef ? readField('id', nextRef) : null),
                  },
                })
              }
            }

            return existingEntityRefs
          },
        },
      })
    },
    [cache],
  )

  const moveStories = useCallback(
    (storyIds: string[], overId: string, activeStoryId: string) => {
      const {
        status: activeStoryStatus,
        storyType: activeStoryType,
        estimate: activeStoryEstimate,
      } = getStory(activeStoryId) ?? {
        status: null,
        storyType: null,
        estimate: -1,
      }
      if (!activeStoryStatus) return new Promise(() => {})
      const overStory = getStory(overId)
      const { group: originGroup, subgroup: originSubgroup } = getGroupName(activeStoryStatus)
      const { group: targetGroup, subgroup: targetSubgroup } = isGroup(overId)
        ? getGroupNameFromKey(overId)
        : getGroupName(overStory?.status, overStory?.subgroup)

      const isValidMove =
        (VALID_MOVE_CONDITIONS.SCHEDULED_TO_UNSCHEDULED(originGroup, targetGroup) ||
          VALID_MOVE_CONDITIONS.ESTIMATABLE_STORY_UNSCHEDULED_TO_SCHEDULED(
            originGroup,
            targetGroup,
            activeStoryType,
            activeStoryEstimate,
          ) ||
          VALID_MOVE_CONDITIONS.NONESTIMATABLE_STORY_UNSCHEDULED_TO_SCHEDULED(
            originGroup,
            targetGroup,
            activeStoryType,
          ) ||
          VALID_MOVE_CONDITIONS.RELEASE_EXCEPT_TO_AND_FROM_ACCEPTED(originGroup, targetGroup, activeStoryType) ||
          VALID_MOVE_CONDITIONS.SAME_GROUP_EXCEPT_ACCEPTED(originGroup, targetGroup)) &&
        VALID_MOVE_CONDITIONS.NOT_SAME_STORY(storyIds, activeStoryId, overId)

      if (
        !isValidMove &&
        originGroup === STORY_GROUPS.UNSCHEDULED &&
        targetGroup === STORY_GROUPS.SCHEDULED &&
        (activeStoryType === STORY_TYPES.FEATURE || activeStoryType === STORY_TYPES.DESIGN) &&
        activeStoryEstimate === -1
      )
        GlobalPubSub.publish(GlobalPubSub.Signals.DISPLAY_STORY_MOVE_ERROR, true)

      const isSubgroupMovement = originSubgroup !== targetSubgroup

      if (!isValidMove) return new Promise(() => {})

      if (
        targetGroup === STORY_GROUPS.SCHEDULED &&
        overStory?.storyType === STORY_TYPES.RELEASE &&
        overStory?.afterId !== null
      ) {
        const storyBefore = getStory(overStory.afterId)
        if (
          storyBefore &&
          storyBefore.storyType !== STORY_TYPES.RELEASE &&
          storyBefore.status !== STORY_STATUSES.UNSTARTED
        ) {
          return new Promise(() => {})
        }
      }
      cache.modify({
        id: cache.identify({ __typename: 'Project', id: projectMeta.id }),
        fields: {
          // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
          stories(existingValue: readonly Reference[] = [], { readField }) {
            const existingEntityRefs = existingValue as Reference[]
            const targetStoryIndexes = storyIds.map((id) =>
              existingEntityRefs.findIndex((ref) => readField('id', ref) === id),
            )
            const storyRefs = targetStoryIndexes.map((index) => existingEntityRefs[index])
            const overStoryRef = existingEntityRefs.find((ref) => readField('id', ref) === overId)
            const activeStoryIndex = existingEntityRefs.findIndex((ref) => readField('id', ref) === activeStoryId)
            const overStoryIndex = existingEntityRefs.findIndex((ref) => ref === overStoryRef)

            const storyRefsStatus = readField('status', storyRefs[0])
            const overStoryRefStatus = readField('status', overStoryRef) ?? getStatusByGroup(targetGroup)
            if (storyRefs.some((ref) => !ref)) return existingEntityRefs

            if (
              (storyRefsStatus === STORY_STATUSES.UNSCHEDULED && overStoryRefStatus === STORY_STATUSES.UNSTARTED) ||
              (storyRefsStatus === STORY_STATUSES.UNSTARTED && overStoryRefStatus === STORY_STATUSES.UNSCHEDULED) ||
              activeStoryType === STORY_TYPES.RELEASE ||
              isSubgroupMovement
            ) {
              for (const ref of storyRefs) {
                cache.modify({
                  id: cache.identify(ref),
                  fields: {
                    status: (prev) => {
                      if (targetGroup === STORY_GROUPS.UNSCHEDULED) {
                        return STORY_STATUSES.UNSCHEDULED
                      }
                      if (targetGroup === STORY_GROUPS.SCHEDULED) {
                        return STORY_STATUSES.UNSTARTED
                      }
                      return prev
                    },
                    subgroup: () => targetSubgroup as StorySubgroup,
                  },
                })
              }
            }

            const newRefList = existingEntityRefs.filter((ref) => !storyRefs.some((storyRef) => storyRef === ref))
            const adjustedSpliceIndex = getSpliceIndex(overStoryIndex, targetStoryIndexes, activeStoryIndex)
            newRefList.splice(adjustedSpliceIndex, 0, ...storyRefs)

            return newRefList
          },
        },
      })

      redefineStoryOrder(projectMeta.id)

      const { afterId } = getStory(storyIds[0]) ?? { afterId: null }
      const { beforeId } = getStory(storyIds[storyIds.length - 1]) ?? { beforeId: null }

      const { variables, optimisticResponse } = getExecuteTemplate(
        projectMeta,
        'STORY_MULTI_MOVE',
        user.id,
        {
          storyIds,
          afterId,
          beforeId,
          group: targetGroup,
          subgroup: targetSubgroup,
        },
        storyIds.map((id) => ({
          ...getStory(id),
          __typename: 'Story',
          afterId,
          beforeId,
          subgroup: targetSubgroup,
        })),
      )

      return execute({
        variables,
        optimisticResponse,
      })
    },
    [redefineStoryOrder, execute, projectMeta, user, cache],
  )

  const moveStoryByStatus = useCallback(
    (storyId: string, targetStatus: StoryStatus, originStatus: StoryStatus) => {
      const projectId = projectMeta.id
      const story = getStory(storyId)
      if (!story) return

      const { group: targetGroupName } = getGroupName(targetStatus)
      const { group: originGroupName } = getGroupName(originStatus)

      if (originGroupName === targetGroupName) return
      cache.modify({
        id: cache.identify({ __typename: 'Project', id: projectId }),
        fields: {
          // biome-ignore lint/style/useDefaultParameterLast: This is a GraphQL cache configuration
          stories: (existingEntityRefs: readonly Reference[] = [], { toReference, readField }) => {
            // biome-ignore lint/style/noNonNullAssertion:
            const storyRef = toReference({ __typename: 'Story', id: storyId })!

            const { accepted, current, scheduled, unscheduled } = groupStories(
              existingEntityRefs,
              readField,
              originStatus,
              storyId,
            )

            switch (true) {
              case targetGroupName === STORY_GROUPS.ACCEPTED:
              case originGroupName === STORY_GROUPS.ACCEPTED && targetGroupName === STORY_GROUPS.CURRENT:
                return [
                  ...[...accepted.filter((ref) => readField('id', ref) !== storyId), storyRef],
                  ...current.filter((ref) => readField('id', ref) !== storyId),
                  ...scheduled.filter((ref) => readField('id', ref) !== storyId),
                  ...unscheduled.filter((ref) => readField('id', ref) !== storyId),
                ]
              case targetGroupName === STORY_GROUPS.SCHEDULED: {
                const lastCurrentElementIndex = current.findLastIndex((ref) => {
                  const status = readField('status', ref)
                  return status !== STORY_STATUSES.UNSCHEDULED && status !== STORY_STATUSES.UNSTARTED
                })
                const newCurrent = current.filter((ref) => readField('id', ref) !== storyId)
                return [
                  ...accepted.filter((ref) => readField('id', ref) !== storyId),
                  ...[
                    ...newCurrent.slice(0, lastCurrentElementIndex + 1),
                    storyRef,
                    ...newCurrent.slice(lastCurrentElementIndex + 1),
                  ],
                  ...scheduled.filter((ref) => readField('id', ref) !== storyId),
                  ...unscheduled.filter((ref) => readField('id', ref) !== storyId),
                ]
              }
              default:
                return [
                  ...accepted.filter((ref) => readField('id', ref) !== storyId),
                  ...[...current.filter((ref) => readField('id', ref) !== storyId), storyRef],
                  ...scheduled.filter((ref) => readField('id', ref) !== storyId),
                  ...unscheduled.filter((ref) => readField('id', ref) !== storyId),
                ]
            }
          },
        },
      })
      redefineStoryOrder(projectId)
    },
    [redefineStoryOrder, cache, projectMeta.id],
  )

  return {
    moveStories,
    redefineStoryOrder,
    moveStoryByStatus,
  }
}

const groupStories = (
  existingEntityRefs: readonly Reference[],
  readField: ModifierDetails['readField'],
  originStatus: StoryStatus,
  storyId: string,
) => {
  let lastPushedGroup: StoryGroup = STORY_GROUPS.ACCEPTED

  return existingEntityRefs.reduce(
    (acc, ref) => {
      const storyType = readField('storyType', ref)
      const status = storyId === readField('id', ref) ? originStatus : readField('status', ref)
      if (!status) return acc

      if (status === STORY_STATUSES.ACCEPTED) {
        acc.accepted.push(ref)
        return acc
      }

      if (storyType === STORY_TYPES.RELEASE) {
        if (readField('subgroup', ref)) {
          acc.unscheduled.push(ref)
          return acc
        }
        if (lastPushedGroup === STORY_GROUPS.SCHEDULED) {
          acc[STORY_GROUPS.SCHEDULED].push(ref)
          return acc
        }
        acc[STORY_GROUPS.CURRENT].push(ref)
        return acc
      }

      const { group: listName } = getGroupName(status as StoryStatus)
      acc[listName].push(ref)
      lastPushedGroup = listName
      return acc
    },
    {
      accepted: [] as Reference[],
      current: [] as Reference[],
      scheduled: [] as Reference[],
      unscheduled: [] as Reference[],
    },
  )
}

const VALID_MOVE_CONDITIONS = {
  SCHEDULED_TO_UNSCHEDULED: (originGroup: StoryGroup, targetGroup: StoryGroup) =>
    originGroup === STORY_GROUPS.SCHEDULED && targetGroup === STORY_GROUPS.UNSCHEDULED,
  ESTIMATABLE_STORY_UNSCHEDULED_TO_SCHEDULED: (
    originGroup: StoryGroup,
    targetGroup: StoryGroup,
    activeStoryType: StoryType,
    activeStoryEstimate: number,
  ) =>
    originGroup === STORY_GROUPS.UNSCHEDULED &&
    targetGroup === STORY_GROUPS.SCHEDULED &&
    (activeStoryType === STORY_TYPES.FEATURE || activeStoryType === STORY_TYPES.DESIGN) &&
    activeStoryEstimate !== -1,
  NONESTIMATABLE_STORY_UNSCHEDULED_TO_SCHEDULED: (
    originGroup: StoryGroup,
    targetGroup: StoryGroup,
    activeStoryType: StoryType,
  ) =>
    originGroup === STORY_GROUPS.UNSCHEDULED &&
    targetGroup === STORY_GROUPS.SCHEDULED &&
    [STORY_TYPES.RELEASE, STORY_TYPES.BUG, STORY_TYPES.CHORE].some((type) => type === activeStoryType),
  RELEASE_EXCEPT_TO_AND_FROM_ACCEPTED: (originGroup: StoryGroup, targetGroup: StoryGroup, activeStoryType: StoryType) =>
    !(originGroup === STORY_GROUPS.ACCEPTED || targetGroup === STORY_GROUPS.ACCEPTED) &&
    activeStoryType === STORY_TYPES.RELEASE,
  SAME_GROUP_EXCEPT_ACCEPTED: (originGroup: StoryGroup, targetGroup: StoryGroup) =>
    originGroup === targetGroup && originGroup !== STORY_GROUPS.ACCEPTED,
  NOT_SAME_STORY: (storyIds: string[], activeStoryId: string, overId: string) =>
    !(storyIds.length === 1 && activeStoryId === overId),
}
